import Component from "@base/Component.js";
import t from "@/services/translationService.js";
import tagController from "./tagController.js";
import { cleanTag, foldTag } from "@/services/tags.js";

/**
 * Create or edit one tag: its two display names, its key, and — when editing —
 * deleting it.
 *
 * Delete lives in here rather than on the table row, matching the gallery and
 * gear editors: a destructive action should be two steps behind a deliberate
 * "open this thing", not one stray click away in a list you were scrolling.
 *
 * The key gets its own boxed section because it is not a label — it is what
 * every gallery stores, so changing it rewrites content. A typed key that
 * already exists silently stops being a rename and becomes a merge, which is
 * worth having (it is the only way to fold two tags that mean the same thing
 * into one) but never worth having unannounced, hence the live note.
 *
 * props: { tag: { key, nameEn, nameDe, count }|null, onSaved() }  — null → create.
 */
export default class TagEditModal extends Component {
  constructor(props) {
    super(props);
    this.isNew = !props.tag;
    this.tag = props.tag || { key: "", nameEn: "", nameDe: "", count: 0 };
  }

  render() {
    const tag = this.tag;
    const heading = this.isNew ? t.t("cockpit.tags.add") : t.t("cockpit.tags.edit");
    return `
      <div class="modal-backdrop">
        <div class="modal-card modal-card--editor" role="dialog" aria-modal="true" aria-label="${Component.escape(heading)}">
          <div class="modal-header">
            <h2>${Component.escape(heading)}</h2>
            <button type="button" class="modal-close" aria-label="${Component.escape(t.t("common.cancel"))}">&times;</button>
          </div>
          <form class="login-form tag-form" novalidate>
            <label>${Component.escape(t.t("cockpit.tags.nameEn"))}
              <input name="nameEn" required value="${Component.escape(tag.nameEn)}" />
            </label>
            <label>${Component.escape(t.t("cockpit.tags.nameDe"))}
              <input name="nameDe" required value="${Component.escape(tag.nameDe)}" />
            </label>
            <fieldset class="tag-form__key">
              <legend>${Component.escape(t.t("cockpit.tags.key"))}</legend>
              <p class="tag-form__hint">${Component.escape(
                this.isNew ? t.t("cockpit.tags.keyHintNew") : t.t("cockpit.tags.keyHint").replace("{count}", tag.count)
              )}</p>
              <label class="tag-form__key-label">
                <input name="key" required value="${Component.escape(tag.key)}" />
              </label>
              <p class="tag-form__merge" role="status" hidden></p>
            </fieldset>
            <div class="form-error" role="alert" hidden></div>
            <div class="editor-actions">
              <button type="submit" class="btn-primary">${Component.escape(t.t("common.save"))}</button>
              ${
                this.isNew
                  ? ""
                  : `<button type="button" class="btn-danger" data-action="delete">${Component.escape(t.t("common.delete"))}</button>`
              }
              <button type="button" class="btn-secondary" data-cancel>${Component.escape(t.t("common.cancel"))}</button>
            </div>
          </form>
        </div>
      </div>`;
  }

  onMounted() {
    const close = () => this.unmount();
    this.listen(this.$(".modal-close"), "click", close);
    this.listen(this.$("[data-cancel]"), "click", close);
    this.listen(this.el, "click", (e) => e.target === this.el && close());
    this.listen(document, "keydown", (e) => e.key === "Escape" && close());
    this.listen(this.$(".tag-form"), "submit", (e) => this.#submit(e));

    const del = this.$('[data-action="delete"]');
    if (del) this.listen(del, "click", () => this.#delete());

    const keyInput = this.$('input[name="key"]');
    this.listen(keyInput, "input", () => this.#reflectConflict(keyInput.value));
    this.#reflectConflict(keyInput.value);

    if (this.isNew) this.$('input[name="nameEn"]').focus();
  }

  /**
   * Say, while typing, what an existing key would mean: a merge when editing,
   * a plain refusal when creating (there is nothing to merge *from* yet).
   */
  #reflectConflict(value) {
    const note = this.$(".tag-form__merge");
    const key = cleanTag(value);
    const target =
      key && foldTag(key) !== foldTag(this.tag.key)
        ? tagController.manager.all().find((row) => foldTag(row.key) === foldTag(key))
        : null;
    note.textContent = target
      ? (this.isNew ? t.t("cockpit.tags.errExists") : t.t("cockpit.tags.mergeWarning")).replace("{tag}", target.key)
      : "";
    note.hidden = !target;
    note.classList.toggle("tag-form__merge--blocking", Boolean(target) && this.isNew);
  }

  async #submit(e) {
    e.preventDefault();
    const form = e.target;
    const err = this.$(".form-error");
    const nameEn = cleanTag(form.nameEn.value);
    const nameDe = cleanTag(form.nameDe.value);
    const key = cleanTag(form.key.value);
    const original = this.tag.key;

    if (!nameEn || !nameDe || !key) return this.#showError(err, t.t("cockpit.tags.errRequired"));

    const submit = this.$('button[type="submit"]');
    submit.disabled = true;
    try {
      if (this.isNew) {
        await tagController.create({ key, nameEn, nameDe });
      } else {
        // Names first, then the key: retag() moves the row's translations with
        // it, so saving in this order means one round trip carries both edits
        // even when the key changes.
        await tagController.updateNames(original, { nameEn, nameDe });
        if (key !== original) await tagController.retag(original, key);
      }
      this.props.onSaved?.();
      this.unmount();
    } catch (ex) {
      submit.disabled = false;
      this.#showError(err, ex.message || t.t("cockpit.tags.errSave"));
    }
  }

  async #delete() {
    // The count is what makes this decision, not the name: deleting strips the
    // tag from every gallery carrying it (the row and the uses go together —
    // there is no such thing as a tag that exists only in the galleries).
    const message = this.tag.count
      ? t.t("cockpit.tags.confirmDeleteUsed").replace("{tag}", this.tag.nameEn).replace("{count}", this.tag.count)
      : t.t("cockpit.tags.confirmDelete").replace("{tag}", this.tag.nameEn);
    if (!window.confirm(message)) return;

    const err = this.$(".form-error");
    try {
      await tagController.remove(this.tag.key);
      this.props.onSaved?.();
      this.unmount();
    } catch (ex) {
      this.#showError(err, ex.message || t.t("cockpit.tags.errSave"));
    }
  }

  #showError(el, msg) {
    el.textContent = msg;
    el.hidden = false;
  }
}
